/**
 * Which career milestone plays which `.TTM` cutscene, and in the original's
 * own nation split.
 *
 * The inventory, from disassembling all six of the corpus's "presentation"
 * scripts directly (`docs/formats/ttm-animation.md`, this lane's own
 * re-check against `packages/formats`' `parseTtm`): `TITLE.TTM` is the game
 * intro (already wired, `shell/intro/`); the other five are `WAREND1.TTM`
 * (loads its own `WAREND1.SNG` — the war's-end scene) and two nation pairs,
 * `B-SAL.TTM`/`G-SAL.TTM` (both load `PROMOTE.SNG` — the promotion salute)
 * and `B-MED.TTM`/`G-MED.TTM` (both load `MEDAL.SNG` plus `SIMSNDS.SNG` for
 * a `PLAY SFX` sting — the medal ceremony). No script anywhere in the real
 * 28-file corpus is a wound, capture, death or Board of Inquiry scene:
 * `docs/game/campaign.md`'s "Wounds, death, and discharge" section and
 * `docs/formats/sound-music.md`'s cutscene-music table both point at
 * `BOI.DAT`'s own text pool and `BOARD.SNG`/`DEATH.SNG`/`TIMEPASS.SNG`
 * playing under the existing consequence-dialog screen instead — this
 * module deliberately does not invent a `.TTM` for any of those.
 */

import type { CareerConsequence, Nation } from "@red-opus/engine";

export type CareerCutsceneKind = "medal" | "promotion" | "warEnd";

const MEDAL_TTM: Readonly<Record<Nation, string>> = { allied: "B-MED.TTM", german: "G-MED.TTM" };
const SALUTE_TTM: Readonly<Record<Nation, string>> = { allied: "B-SAL.TTM", german: "G-SAL.TTM" };

export interface CareerCutsceneScript {
  /** `data/baked/cutscenes/<ttm>` — see `tools/bake/src/stages/cutscene-scripts.ts`. */
  readonly ttm: string;
  /** `MUSIC_PATHS` key (`shell/audio/shell-audio.ts`). */
  readonly song: string;
}

export function careerCutsceneScript(kind: CareerCutsceneKind, nation: Nation): CareerCutsceneScript {
  if (kind === "medal") return { ttm: MEDAL_TTM[nation], song: "MEDAL" };
  if (kind === "promotion") return { ttm: SALUTE_TTM[nation], song: "PROMOTE" };
  return { ttm: "WAREND1.TTM", song: "WAREND1" };
}

/**
 * The queue of milestone scenes one sortie's consequence earned, in the
 * order the original's own between-missions sequence implies (decorations
 * before the promotion score they contributed to, `apply-debrief.ts`'s own
 * ordering note; war's end last because it is the career's very last
 * consequence). A death or discharge ending is never queued here — see this
 * module's header — so `consequence.ending` only ever contributes when its
 * status is `"retired"`, the armistice ending.
 */
export function cutsceneQueueFor(
  consequence: Pick<CareerConsequence, "awards" | "promotion" | "ending">,
): readonly CareerCutsceneKind[] {
  const queue: CareerCutsceneKind[] = [];
  if (consequence.awards.length > 0) queue.push("medal");
  if (consequence.promotion) queue.push("promotion");
  if (consequence.ending?.status === "retired") queue.push("warEnd");
  return queue;
}
